import { useDiet } from "@/contexts/DietContext";
import { Button } from "@/components/ui/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Star, Zap, Crown, Lock, Loader2, Pencil, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { apiFetch } from "@/lib/api";
import { hasPendingDietPreferences, loadPendingDietPreferences } from "@/lib/pendingDietCheckout";
import { useAuth } from "@/contexts/AuthContext";
import PlanEditDialog from "@/components/plans/PlanEditDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface PlanConfig {
  id: string;
  name: string;
  price: string;
  period: string;
  features: string[];
  locked: string[];
  highlight: boolean;
  badge: string;
  sort_order: number;
}

const ICON_MAP: Record<string, React.ComponentType<any>> = {
  basic: Star,
  plus: Zap,
  complete: Crown,
};

const Plans = () => {
  const { selectedPlan, refreshSubscription, measures, meals } = useDiet();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanConfig[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PlanConfig | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);

  // Gate: bloqueia checkout sem IMC e sem dieta com alimentos selecionados.
  // Quem já tem assinatura ativa pula a validação (está renovando/trocando).
  const ensureOnboardingComplete = (): boolean => {
    if (!user) {
      toast.info("Faça login ou crie sua conta para assinar um plano.");
      navigate(`/login?next=${encodeURIComponent("/plans")}`);
      return false;
    }
    if (hasActiveSubscription) return true;

    const hasMeasures = !!(
      measures &&
      measures.weight &&
      measures.height &&
      measures.age &&
      measures.calories
    );
    if (!hasMeasures) {
      toast.info("Preencha seus dados corporais antes de assinar um plano.");
      navigate("/dashboard");
      return false;
    }

    const hasSelectedFoodsInContext = meals
      ? Object.values(meals).some((opts: string[][]) => opts.flat().length > 0)
      : false;
    // Também aceita preferências pendentes (sessionStorage/localStorage), pois usuário
    // recém-logado pode ter selecionado os alimentos antes do cadastro/login e ainda
    // não ter currentDietId hidratando `meals` no DietContext.
    const pendingPrefs = loadPendingDietPreferences();
    const hasPendingFoods = pendingPrefs
      ? Object.values(pendingPrefs).some((ids) => Array.isArray(ids) && ids.length > 0)
      : false;
    if (!hasSelectedFoodsInContext && !hasPendingFoods) {
      toast.info("Monte sua dieta selecionando alimentos antes de assinar um plano.");
      navigate("/diet-builder");
      return false;
    }

    return true;
  };


  const loadSubscriptionStatus = () => {
    if (!user) return; // visitantes não têm assinatura
    apiFetch<{ has_active_subscription: boolean }>("api-subscription", undefined, { method: "GET" })
      .then((d) => setHasActiveSubscription(!!d.has_active_subscription))
      .catch(() => {});
  };
  useEffect(() => { loadSubscriptionStatus(); }, [user]);

  // Load plans from DB — endpoint público, funciona para visitantes
  useEffect(() => {
    const loadPlans = async () => {
      try {
        const SUPABASE_URL = "https://siypiyiaqxqyzztwpcao.supabase.co";
        const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNpeXBpeWlhcXhxeXp6dHdwY2FvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzA4NjI0MjYsImV4cCI6MjA4NjQzODQyNn0.SY3vD38jOG6_sZKC4JzAAQ13aXju2I7Ldk-EyOZxvSY";
        const res = await fetch(`${SUPABASE_URL}/functions/v1/api-plan-configs?action=list`, {
          headers: { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data)) setPlans(data);
      } catch {
        // silencioso
      }
    };
    loadPlans();
  }, []);

  // Check admin (apenas se autenticado)
  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    apiFetch<{ isAdmin: boolean }>("api-plan-configs", { action: "check-admin" })
      .then((d) => setIsAdmin(d.isAdmin))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    const success = searchParams.get("success");
    const plan = searchParams.get("plan");
    const canceled = searchParams.get("canceled");

    if (success === "true" && plan) {
      const hasPending = hasPendingDietPreferences();
      const loadingId = toast.loading(
        hasPending
          ? "Pagamento confirmado! Ativando seu plano para gerar a dieta..."
          : "Pagamento confirmado! Ativando seu plano..."
      );

      const handleSuccess = async () => {
        // Poll until webhook updates the subscription (avoids race condition).
        const MAX_ATTEMPTS = 15; // ~30s
        let activated = false;
        for (let i = 0; i < MAX_ATTEMPTS; i++) {
          try {
            const res = await apiFetch<{ plan?: string; diet_credits?: number }>("api-subscription");
            const isActive = (res?.plan && res.plan !== "free") || (res?.diet_credits ?? 0) > 0;
            if (isActive) { activated = true; break; }
          } catch {}
          await new Promise(r => setTimeout(r, 2000));
        }
        await refreshSubscription();
        toast.dismiss(loadingId);

        if (!activated) {
          toast.error("O pagamento ainda não foi confirmado. Aguarde alguns instantes e tente novamente.");
          navigate("/plans", { replace: true });
          return;
        }

        toast.success("Plano ativado com sucesso!");
        if (hasPending) {
          navigate("/diet-builder?auto=true&payment=success", { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      };
      handleSuccess();
    } else if (canceled === "true") {
      toast.info("Pagamento cancelado.");
      navigate("/plans", { replace: true });
    }
  }, [searchParams, navigate, refreshSubscription]);

  const handleSelect = async (planId: string) => {
    if (!ensureOnboardingComplete()) return;
    setPurchasing(planId);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { plan: planId, returnToDietBuilder: hasPendingDietPreferences() },
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("URL de checkout não recebida");
      }
    } catch (e) {
      console.error("Checkout error:", e);
      toast.error("Erro ao iniciar pagamento. Tente novamente.");
    } finally {
      setPurchasing(null);
    }
  };

  const handleSavePlan = async (updated: PlanConfig) => {
    try {
      const saved = await apiFetch<PlanConfig>("api-plan-configs", undefined, {
        method: "PUT",
        body: updated,
      });
      setPlans((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
      toast.success("Plano atualizado com sucesso!");
      setEditingPlan(null);
    } catch (e) {
      console.error("Save plan error:", e);
      toast.error("Erro ao salvar plano.");
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-foreground">Escolha seu Plano</h1>
        <p className="text-muted-foreground mt-1">Comece sua transformação hoje</p>
        <p className="text-muted-foreground text-xs mt-1">Pagamento seguro via InfinitePay — Cartão de Crédito e PIX</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 max-w-sm lg:max-w-none mx-auto">
        {plans.map((plan, i) => {
          const IconComp = ICON_MAP[plan.id] || Star;
          return (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="relative bg-card rounded-2xl p-6 border-2 transition-all flex flex-col border-border shadow-card"
            >
              {/* Admin edit button */}
              {isAdmin && (
                <button
                  onClick={() => setEditingPlan(plan)}
                  className="absolute top-3 right-3 p-1.5 rounded-lg bg-muted/80 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors z-10"
                  title="Editar plano"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}

              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="gradient-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-3">
                  <img src="/logo_final.png" alt="Nutrir Corpo & Mente" className="w-10 h-10 object-contain" />
                </div>
                <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
                <div className="mt-2">
                  <span className="text-3xl font-extrabold text-foreground">{plan.price}</span>
                </div>
                <span className="inline-block mt-2 text-xs font-medium text-muted-foreground border border-border rounded-full px-3 py-1">
                  {plan.period}
                </span>
              </div>


              <ul className="space-y-2.5 mb-4">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-foreground">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              {plan.locked.length > 0 && (
                <ul className="space-y-2 mb-6 pt-3 border-t border-border">
                  {plan.locked.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground/50">
                      <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-auto pt-4 space-y-2">
                {plan.id === "complete" && hasActiveSubscription ? (
                  <>
                    <Button disabled className="w-full" variant="outline">
                      Assinatura Ativa
                    </Button>
                    <Button
                      onClick={() => setCancelOpen(true)}
                      variant="ghost"
                      className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      Cancelar assinatura
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => handleSelect(plan.id)}
                    disabled={purchasing !== null}
                    className="w-full gradient-primary text-primary-foreground hover:opacity-90"
                  >
                    {purchasing === plan.id ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processando...</>
                    ) : plan.id === "complete" ? (
                      "Assinar Plano"
                    ) : selectedPlan === plan.id && plan.id === "basic" ? (
                      "Comprar mais crédito"
                    ) : selectedPlan === plan.id ? (
                      "Renovar Plano"
                    ) : (
                      "Escolher Plano"
                    )}
                  </Button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar assinatura PRO FIT MAX?</AlertDialogTitle>
            <AlertDialogDescription>
              Seu acesso continua válido até o fim do período já pago. A renovação
              automática no Stripe será cancelada agora e você não será cobrada(o)
              no próximo ciclo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={canceling}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              disabled={canceling}
              onClick={async (e) => {
                e.preventDefault();
                setCanceling(true);
                try {
                  await apiFetch("cancel-subscription", undefined, { method: "POST" });
                  toast.success("Assinatura cancelada. Acesso mantido até o fim do período.");
                  setHasActiveSubscription(false);
                  await refreshSubscription();
                  setCancelOpen(false);
                } catch (err: any) {
                  toast.error(err?.message || "Erro ao cancelar assinatura");
                } finally {
                  setCanceling(false);
                }
              }}
            >
              {canceling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cancelando...</> : "Cancelar assinatura"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Admin edit dialog */}
      {editingPlan && (
        <PlanEditDialog
          plan={editingPlan}
          open={!!editingPlan}
          onClose={() => setEditingPlan(null)}
          onSave={handleSavePlan}
        />
      )}
    </motion.div>
  );
};

export default Plans;
